// src/components/charts/base/TreemapChart.tsx
/**
 * TreemapChart Component
 * 
 * A reusable treemap visualization with support for:
 * - Proportional rectangles based on value
 * - Color encoding based on secondary metric (e.g., change %)
 * - Diverging color scheme for positive/negative values
 * - Customizable labels and formatting
 * - Consistent dark theme styling
 * 
 * @example
 * // Market cap treemap with 7d change coloring
 * <TreemapChart
 *   nodes={[
 *     { name: 'BTC', value: 1200000000000, colorValue: 0.05 },
 *     { name: 'ETH', value: 400000000000, colorValue: -0.02 },
 *   ]}
 *   sizeLabel="Market Cap"
 *   colorLabel="7D Change"
 *   colorIsPercent
 * />
 */

import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { 
  CHART_COLORS, 
  getSemanticColors, 
  interpolateColor,
} from '@/utils/charts/theme';
import { formatCurrencyShort, formatPercent } from '@/utils/charts/formatters';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TreemapNode {
  /** Display name */
  name: string;
  /** Primary value (determines rectangle size) */
  value: number;
  /** Optional secondary value for color encoding */
  colorValue?: number;
  /** Optional category for grouping */
  category?: string;
}

export type TreemapValueFormat = 'currency' | 'percent' | 'number' | 'compact';

// Default colors from unified theme (deep variant for large areas)
const DEEP_SEMANTIC = getSemanticColors('deep');

export interface TreemapChartProps {
  /** Array of treemap nodes */
  nodes: TreemapNode[];
  /** Chart height in pixels */
  height?: number;
  /** Label for size metric (shown in tooltip) */
  sizeLabel?: string;
  /** Label for color metric (shown in tooltip) */
  colorLabel?: string;
  /** Whether colorValue is a decimal percent (0.05 = 5%) */
  colorIsPercent?: boolean;
  /** Format for size values */
  sizeFormat?: TreemapValueFormat;
  /** Show labels on treemap rectangles */
  showLabels?: boolean;
  /** Custom color scheme - positive color (defaults to deep semantic positive) */
  positiveColor?: string;
  /** Custom color scheme - negative color (defaults to deep semantic negative) */
  negativeColor?: string;
  /** Custom color scheme - neutral color (defaults to deep semantic neutral) */
  neutralColor?: string;
  /** Additional className */
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getValueFormatter(format: TreemapValueFormat): (v: number) => string {
  switch (format) {
    case 'currency':
      return formatCurrencyShort;
    case 'percent':
      return (v: number) => formatPercent(v, { decimals: 2 });
    case 'compact':
      return (v: number) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(v);
    default:
      return (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const TreemapChart: React.FC<TreemapChartProps> = ({
  nodes,
  height = 400,
  sizeLabel = 'Value',
  colorLabel = 'Change',
  colorIsPercent = false,
  sizeFormat = 'currency',
  showLabels = true,
  positiveColor = DEEP_SEMANTIC.positive,
  negativeColor = DEEP_SEMANTIC.negative,
  neutralColor = DEEP_SEMANTIC.neutral,
  className = '',
}) => {
  const sizeFormatter = getValueFormatter(sizeFormat);
  
  // Calculate color range
  const colorValues = nodes.map(n => n.colorValue).filter((v): v is number => v !== undefined);
  const hasColorValues = colorValues.length > 0;
  const minColor = hasColorValues ? Math.min(...colorValues) : 0;
  const maxColor = hasColorValues ? Math.max(...colorValues) : 0;
  
  const option = useMemo(() => {
    // Build treemap data with colors based on change intensity
    const treemapData = nodes.map(node => {
      let itemColor: string = CHART_COLORS.series[0] as string;
      
      if (hasColorValues && node.colorValue !== undefined) {
        const val = node.colorValue;
        
        if (minColor < 0 && maxColor > 0) {
          // Values span positive/negative - interpolate based on intensity
          if (val >= 0) {
            const intensity = maxColor > 0 ? Math.min(val / maxColor, 1) : 0;
            itemColor = interpolateColor(neutralColor, positiveColor, 0.4 + intensity * 0.6);
          } else {
            const intensity = minColor < 0 ? Math.min(Math.abs(val) / Math.abs(minColor), 1) : 0;
            itemColor = interpolateColor(neutralColor, negativeColor, 0.4 + intensity * 0.6);
          }
        } else {
          // All same sign or no variation - use gradient
          const normalizedValue = maxColor !== minColor 
            ? (val - minColor) / (maxColor - minColor) 
            : 0.5;
          const colorIndex = Math.floor(normalizedValue * (CHART_COLORS.series.length - 1));
          itemColor = (CHART_COLORS.series[colorIndex] ?? CHART_COLORS.series[0]) as string;
        }
      }
      
      return {
        name: node.name,
        value: node.value,
        colorValue: node.colorValue,
        itemStyle: {
          color: itemColor,
        },
      };
    });
    
    return {
      backgroundColor: CHART_COLORS.background,
      tooltip: {
        backgroundColor: 'rgba(20,20,28,0.95)',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: [10, 14],
        textStyle: {
          color: CHART_COLORS.text.primary,
          fontSize: 12,
        },
        formatter: (params: unknown) => {
          const p = params as { name: string; value: number; data: { colorValue?: number } };
          let html = `<div style="font-weight:500;margin-bottom:4px">${p.name}</div>`;
          html += `<div style="color:rgba(255,255,255,0.6);font-size:11px">`;
          html += `${sizeLabel}: ${sizeFormatter(p.value)}`;
          if (p.data.colorValue !== undefined && p.data.colorValue !== null) {
            // colorValue may be a decimal (0.0044 = 0.44%) that needs multiplication
            const displayValue = colorIsPercent ? p.data.colorValue * 100 : p.data.colorValue;
            const changePrefix = displayValue >= 0 ? '+' : '';
            const formatted = colorIsPercent 
              ? `${changePrefix}${displayValue.toFixed(2)}%`
              : displayValue.toFixed(2);
            html += `<br/>${colorLabel}: ${formatted}`;
          }
          html += `</div>`;
          return html;
        },
      },
      series: [{
        type: 'treemap' as const,
        data: treemapData,
        width: '100%',
        height: '100%',
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        label: {
          show: showLabels,
          formatter: '{b}',
          color: CHART_COLORS.text.primary,
          fontSize: 12,
          fontWeight: 600,
          textShadowColor: 'rgba(0,0,0,0.5)',
          textShadowBlur: 4,
        },
        upperLabel: { show: false },
        itemStyle: {
          borderColor: 'rgba(15,15,25,1)',
          borderWidth: 2,
          borderRadius: 3,
        },
        levels: [{
          itemStyle: {
            borderColor: 'rgba(15,15,25,1)',
            borderWidth: 3,
            gapWidth: 3,
          },
        }],
      }],
      animationDuration: 500,
      animationEasing: 'cubicOut' as const,
    } as EChartsOption;
  }, [nodes, sizeLabel, colorLabel, colorIsPercent, sizeFormatter, showLabels, hasColorValues, minColor, maxColor, positiveColor, negativeColor, neutralColor]);

  return (
    <div className={className}>
      <ReactECharts
        option={option}
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
        lazyUpdate
      />
    </div>
  );
};

export default TreemapChart;

